<template>
    <div class="q-px-md q-py-md" v-if="!dense">
        <q-linear-progress size="20px" :value="fixedFloat(remaining / 100)" color="primary" :animation-speed="200">
            <div class="absolute-full flex flex-center">
                <q-badge color="white" text-color="primary" :label="remaining.toFixed(2) + '%'" />
            </div>
        </q-linear-progress>
    </div>

    <q-item>
        <q-item-section side>
            <q-icon 
                :name="remaining > 0 ? 'mdi-alert' : 'mdi-check-circle'"
                :color="remaining > 0 ? 'deep-orange' : 'green'"
            />
            <q-tooltip :class="remaining > 0 ? 'bg-deep-orange' : 'bg-green'">
                <template v-if="remaining > 0">
                    <div>Remaining: {{ remaining.toFixed(2) }}%</div>
                    <div>Total: {{ total.toFixed(2) }}%</div>
                </template>
                <div v-else>
                    All splits are complete!
                </div>
            </q-tooltip>
        </q-item-section>
        <q-item-section>
            <q-item-label caption>
                Remaining
            </q-item-label>
            <q-item-label class="text-h6">
                {{ remaining.toFixed(2) + '%' }}
            </q-item-label>
        </q-item-section>
        <q-item-section side>
            <q-btn
                :dense="dense"
                flat
                no-caps
                icon="mdi-cancel"
                label="Reset"
                color="negative"
                @click="resetSplits()"
            />
        </q-item-section>
        <q-item-section side>
            <q-btn
                :dense="dense"
                flat
                no-caps
                icon="mdi-chart-pie"
                label="Split Evenly"
                color="primary"
                @click="handleEvenSplit()"
            />
        </q-item-section>
        <q-item-section v-if="false" side>
            <q-btn
                :dense="dense"
                flat
                no-caps
                icon="mdi-clock-fast"
                label="Set Timeclocks"
                color="deep-orange-5"
            >
                <q-menu>
                    <q-list dense>
                        <q-item>
                            <q-item-section>
                                <q-input
                                    v-model="timeclocks.start"
                                    label="Clock In"
                                    type="date"
                                    mask="YYYY-MM-DD HH:mm:00"
                                    :rules="[val => !!val || 'Time is required']"
                                />
                            </q-item-section>
                            <q-item-section>
                                <q-input
                                    v-model="timeclocks.start"
                                    label="Clock In"
                                    type="date"
                                    mask="datetime"
                                    :rules="[val => !!val || 'Time is required']"
                                />
                            </q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section>
                                <q-input
                                    v-model="timeclocks.end"
                                    label="Clock Out"
                                    type="date"
                                    mask="YYYY-MM-DD HH:mm:00"
                                    :rules="[val => !!val || 'Time is required']"
                                />
                            </q-item-section>
                        </q-item>
                        <q-item>
                            <q-item-section>
                                <q-btn
                                    :dense="dense"
                                    flat
                                    no-caps
                                    icon="mdi-check"
                                    label="Set Timeclocks"
                                    color="primary"
                                />
                            </q-item-section>
                            <q-item-section side>
                                <q-btn
                                    :dense="dense"
                                    flat
                                    no-caps
                                    icon="mdi-close"
                                    label="Cancel"
                                    color="negative"
                                    @click="$refs.menu.hide()"
                                />
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-menu>
            </q-btn>
        </q-item-section>
    </q-item>

    <q-separator />

    <q-list :dense="dense" :separator="$q.dark.isActive">
        <WorkerSplitRow
            v-for="(pay, i) in workerSplits"
            :key="i"
            :pay="pay"
            :remaining="remaining"
            :errors="errors.timeclock"
            :class="{ 'bg-grey-2': i % 2 === 0 && !$q.dark.isActive }"
            @update:pay="val => handleUpdatePay(i, val)"
            @update:total="val => $emit('update:total', val)"
            @update:remaining="val => $emit('update:remaining', val)"
            @delete="val => handleCheckbox(false, val)"
            :work-start-at="workStartAt"
        />
    </q-list>
</template>

<script>
import WorkerSplitRow from './WorkerSplitRow.vue';
import Avatar from '@/Components/Avatar.vue';

export default {
    name: 'WorkerSplitModel',
    emits: [
        'update:workerSplits',
        'update:total',
        'update:remaining',
        'delete'
    ],
    components: {
        Avatar,
        WorkerSplitRow,
    },
    props: {
        dense: {
            type: Boolean,
            default: false,
        },
        workerSplits: {
            type: Array,
            required: true
        },
        emitDelete: {
            type: Boolean,
            default: false,
        },
        workStartAt: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            total: 0,
            lastSum: 0,
            remaining: 0,
            errors: {
                timeclock: [],
            },
            timeclocks: {
                startTime: this.workStartAt,//this.formatTo('', this.workStartAt),
                endTime: this.workStartAt,

                startDate: this.workStartAt,
                endDate: this.workStartAt,
            },
        }
    },
    methods: {
        resetSplits() {
            if (!window.confirm('Are you sure you want to reset all worker splits?')) return;

            let temp = [...this.workerSplits];

            temp.forEach(l => {
                l.split = 0;
            });

            this.total = 0;
            this.lastSum = 0;
            this.remaining = 100;

            this.$emit('update:workerSplits', temp);
        },
        handleSums() {
            this.total = this.splitSum();
            this.lastSum = this.splitSum();
            this.remaining = 100 - this.splitSum();

            this.$emit('update:total', this.total);
            this.$emit('update:remaining', this.remaining);
        },
        handleCheckbox(val, worker) {
            let temp = [...this.workerSplits];

            if (!val) {
                if (this.emitDelete) {
                    this.$emit('delete', worker.id);
                } else {
                    temp = temp.filter(l => l.labor_id !== worker.id);
                }
            } else {
                temp.push({
                    worker: worker,
                    labor_id: worker.id,
                    pay: 0.0,
                    split: 0.0,
                });
            }

            this.$emit('update:workerSplits', temp);
        },
        handleUpdatePay(index, val) {
            let temp = [...this.workerSplits];

            temp[index] = val;

            this.$emit('update:workerSplits', temp);
        },
        handleInput(index, percent) {
            const realRemaining = percent - this.workerSplits[index].split;
            // Check if the percent is greater than the remaining
            if (realRemaining > this.remaining) {
                this.$q.notify({
                    message: `Worker split cannot be greater than ${this.remaining.toFixed(2)}%`,
                    color: 'negative',
                    icon: 'mdi-alert-circle-outline',
                    position: 'top',
                    timeout: 2000,
                });
                return;
            }
            // Handle remaining
            this.handleSlider(index, percent);
        },
        handleSlider(index, percent) {
            let temp = [...this.workerSplits];
            
            temp[index].split = this.fixedFloat(percent);

            this.$emit('update:workerSplits', temp);
        },
        handleEvenSplit() {
            if (!window.confirm('Are you sure you want to split evenly?')) return;

            let total = 0;
            let remainder = 100;

            let temp = [...this.workerSplits];

            temp.forEach(l => {
                let split = this.fixedFloat(100 / temp.length);
                remainder = this.fixedFloat(100 - total);

                if (remainder < split) {
                    split = remainder;
                }

                if (total > 100) {
                    split = 0;
                }

                l.split = split;
                total += split;
            });
            // Check if there's any remaining
            if (total < 100) {
                // Add to the first lumper
                temp[0].split = this.fixedFloat(
                    temp[0].split + this.fixedFloat(100 - total)
                );
            }

            this.$emit('update:workerSplits', temp);
        },
        splitSum() {
            return this.fixedFloat(
                this.workerSplits
                    .map(l => this.fixedFloat(l.split))
                    .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)
            );
        },
        setTimeclocks(start, end) {
            return;
            let temp = [...this.workerSplits];

            temp.forEach(l => {
                l.start = start;
                l.end = end;
            });

            this.$emit('update:workerSplits', temp);
        }
    },
    watch: {
        workerSplits: {
            handler() {
                this.handleSums();
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>